/**
 * Single source of truth for auth error-code → copy. Consolidated 2026-07-18 (magic_link_
 * protection thread, audit): the magic-link error map was hand-copied into MagicLinkForm,
 * SignInCard, and ovid's login page — the last of which never got the 'session-expired' entry
 * added to the other two and silently fell back to generic copy. Every render site imports the
 * two lookups below instead of keeping its own map; a per-product override belongs here only
 * where the copy genuinely differs by brand voice, not as a hand-copied drift.
 */

/** Codes surfaced via `?magic-link-error=<code>` from the magic-link send action
 *  (magic-link.server.ts's handleMagicLinkAction). */
export const MAGIC_LINK_ERRORS: Record<string, string> = {
    'invalid-email': 'Enter a valid email.',
    'rate-limited': 'Too many attempts. Try again in a few minutes.',
    'session-error': 'Session error. Please refresh and try again.',
    'send-failed': 'Failed to send email. Please try again.',
    'session-expired': 'Your session expired. Refresh the page and try again.',
}

/** Codes surfaced via `?auth-error=<code>` from the OAuth callback or the magic-link verify
 *  action (magic-link-verify.server.ts's magicLinkVerifyAction). */
export const AUTH_ERRORS: Record<string, string> = {
    'link-expired': 'Your sign-in link has expired. Request a new one.',
    'oauth-failed': 'Sign-in was interrupted. Please try again.',
    // access_denied at the provider: the visitor chose to stop there.
    'oauth-cancelled': 'Sign-in was cancelled. Try again, or use your email.',
    // A provider identity whose email the provider does not vouch for met an account under that
    // email (Google unverified; Facebook always, as it vouches for none).
    'email-unverified': 'An account already uses this email. Sign in with your email instead.',
    // Facebook gave no email (an account registered by phone, or the permission declined).
    'email-missing': 'Facebook did not share an email address. Sign in with your email instead.',
    // The in-app-browser handoff link was used, expired, or its session signed in meanwhile.
    'handoff-expired': 'That link has expired. Go back to the app and open it again, or sign in here.',
    // The token was valid and already consumed when something else failed (DB write, session
    // link, ...) — 'link-expired' copy would tell the user to do something that can't work
    // (their link is genuinely burned either way, but the failure wasn't the link's fault, and
    // retrying the same broken step won't help either). See magicLinkVerifyAction's second catch.
    'server-error': 'Something went wrong. Please request a new link.',
}

const DEFAULT_MAGIC_LINK_ERROR = 'Something went wrong. Please try again.'
const DEFAULT_AUTH_ERROR = 'Sign-in failed. Please try again.'

/** Looks up magic-link-error copy; null when no code is present. Unmapped codes still surface a
 *  generic message rather than rendering nothing (the pre-consolidation behaviour in
 *  MagicLinkForm silently swallowed unmapped codes). */
export function magicLinkErrorCopy(code: string | null | undefined): string | null {
    if (!code) return null
    return MAGIC_LINK_ERRORS[code] ?? DEFAULT_MAGIC_LINK_ERROR
}

/** Looks up auth-error copy; null when no code is present. */
export function authErrorCopy(code: string | null | undefined): string | null {
    if (!code) return null
    return AUTH_ERRORS[code] ?? DEFAULT_AUTH_ERROR
}
